type ExpiringWeeklyDeal = {
  id?: string
  endsAt: string
}

/**
 * Keeps server-provided weekly-deal pricing honest when a page remains open
 * across the deal's end time. The backend only returns currently active deals,
 * so this composable only needs to schedule their expiry.
 */
export function useWeeklyDealExpiry<T extends ExpiringWeeklyDeal>(
  deal: MaybeRefOrGetter<T | null | undefined>,
) {
  const isActive = ref(Boolean(toValue(deal)))
  let expiryTimer: ReturnType<typeof setTimeout> | null = null
  let mounted = false

  function clearExpiryTimer() {
    if (expiryTimer) clearTimeout(expiryTimer)
    expiryTimer = null
  }

  function syncExpiry() {
    clearExpiryTimer()
    const currentDeal = toValue(deal)
    if (!currentDeal) {
      isActive.value = false
      return
    }

    const remaining = new Date(currentDeal.endsAt).getTime() - Date.now()
    isActive.value = Number.isFinite(remaining) && remaining > 0
    if (!import.meta.client || !isActive.value) return

    expiryTimer = setTimeout(() => {
      isActive.value = false
      expiryTimer = null
    }, Math.min(remaining, 2_147_483_647))
  }

  watch(
    () => {
      const currentDeal = toValue(deal)
      return currentDeal ? `${currentDeal.id ?? ''}:${currentDeal.endsAt}` : null
    },
    () => {
      if (mounted) syncExpiry()
    },
  )
  onMounted(() => {
    mounted = true
    syncExpiry()
  })
  onBeforeUnmount(() => {
    mounted = false
    clearExpiryTimer()
  })

  return readonly(isActive)
}
